/* import __COLOCATED_TEMPLATE__ from './sq-button.hbs'; */
/**
Use `sq-button` to get styling like primary, secondary, destructive, and alternative
without having to add all the individual CSS classes. To see these styles,
check out [button styling](/glass/docs/core-styling/buttons.html).

```example
@hbs
<div style="padding: 5px;">
  {{sq-button label="Default"}}
  {{sq-button rank="primary"
              label="Primary"}}
  {{sq-button rank="secondary"
              label="Secondary"}}
  {{sq-button rank="tertiary"
              label="Tertiary"}}
</div>
<div style="padding: 5px;">
  {{sq-button rank="primary"
              variant="destructive"
              label="Destructive Primary"}}
  {{sq-button rank="secondary"
              variant="destructive"
              label="Destructive Secondary"}}
</div>
<div style="padding: 5px;">
  {{sq-button rank="primary"
              variant="onboarding"
              label="Onboarding Primary"}}
  {{sq-button rank="secondary"
              variant="onboarding"
              label="Onboarding Secondary"}}
</div>
<div style="padding: 5px;">
  {{sq-button variant="suggestion"
              label="ML Suggestion"}}
</div>
```
```example
@title Alternative Variant
@description The alternative variant is used for buttons on a dark background

@hbs
<div style="background-color: #333b43; padding: 10px;">
  {{sq-button rank="primary"
              variant="alternative"
              label="Alternative Primary"}}
  {{sq-button rank="secondary"
              variant="alternative"
              label="Alternative Secondary"}}
</div>
```

```example
@title Button with Caret
@description use hasCaret to show a caret

@hbs
{{sq-button label="Caret"
            hasCaret=true}}
```

```example
@title Button with Loading Spinner
@description Additionally, sq-button has spinner functionality and customization built in.

@hbs
{{sq-button rank="primary"
            isShowingSpinner=true}}
{{sq-button isShowingSpinner=true
            spinnerColor="green"}}
```

### Parameters
@param {string} [rank="secondary"] The action hierarchy for this button. Will be used to apply appropriate class names to the button. 'primary' or 'secondary'
@param {string} [variant] Optional. 'destructive' or 'alternative'. Destructive uses a red color and indicated dangerous actions. Alternative styles are inverted for use on dark backgrounds.
@param {string} [typeAttr] override for button type attribute. When not set, type attribute defaults to "submit" when the rank is primary and "button" otherwise
@param {string} [type] (deprecated - use will cause `computed-property.override` deprecations) button type attribute. Use `typeAttr` instead if the default is not what you need.
@param {boolean} [disabled=false] Determines whether or not the button is disabled
@param {string} [label] If passed, this property will be used as the button text and the instance will be assumed non-block. You can forgo this property and the block form of the component will be treated as the button text
@param {boolean} [isShowingSpinner=false] Will ignore label or block text and show a loading spinner on the button
@param {string} [spinnerColor="blue-medium"] Specify the color for the slide on the spinner. Values are any svg-icon--theme color keyword
@param {string} [spinnerTrackColor="base"] Specify the color for the track on the spinner. Values are either 'base' or 'white'.
@param {string} [hasCaret=false] Setting this to true will show a down caret on the button.
**/

import Component from '@ember/component';
import { computed, set } from '@ember/object';
export const BUTTON_RANK = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary'
};
export const BUTTON_VARIANT = {
  DESTRUCTIVE: 'destructive',
  ALTERNATIVE: 'alternative'
};

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  tagName: 'button',
  attributeBindings: ['disabled', 'testSelector:data-test-button', 'isShowingSpinner:data-test-button-loading', 'type'],
  classNames: ['button'],
  classNameBindings: ['rankClass', 'variantClass', 'hasCaret:caret-down', 'isShowingSpinner:button--loading'],
  rank: BUTTON_RANK.SECONDARY,
  variant: null,
  disabled: false,
  label: null,
  spinnerColor: 'blue-medium',
  spinnerTrackColor: 'base',
  isShowingSpinner: false,
  hasCaret: false,
  type: computed('_type', 'rank', 'typeAttr', {
    get() {
      if (this._type !== undefined) {
        return this._type;
      }
      if (this.get('typeAttr')) {
        return this.get('typeAttr');
      }
      if (this.get('rank') === BUTTON_RANK.PRIMARY) {
        return 'submit';
      } else {
        return 'button';
      }
    },
    set(_, value) {
      return set(this, '_type', value);
    }
  }),
  didReceiveAttrs() {
    this._super();
    if (
    // eslint-disable-next-line no-prototype-builtins
    !this.hasOwnProperty('spinnerColor') && this.get('rank') === 'primary') {
      this.set('spinnerColor', 'white');
      this.set('spinnerTrackColor', 'white');
    }
  },
  rankClass: computed('rank', function () {
    if (this.rank) {
      return `button--${this.rank}`;
    } else {
      return '';
    }
  }),
  variantClass: computed('variant', function () {
    if (this.variant) {
      return `button--${this.variant}`;
    } else {
      return '';
    }
  }),
  testSelector: computed('rank', function () {
    if (this.get('rank')) {
      return this.get('rank');
    } else {
      return true;
    }
  })
});